import { useEffect, useRef, useState } from "react";
import { Upload, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";

const EMPTY = { name: "", description: "", category: "Apparel", image: "", colors: "", sizes: "", points: 100, active: true };
const inputCls = "mt-1.5 w-full h-10 px-3 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#4285F4]";
const toForm = (p) => (p ? { ...p, colors: p.colors.join(", "), sizes: p.sizes.join(", ") } : EMPTY);
const split = (s) => s.split(",").map((x) => x.trim()).filter(Boolean);

const resizeImage = (file) =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      const scale = Math.min(1, 900 / img.width);
      const c = Object.assign(document.createElement("canvas"), { width: img.width * scale, height: img.height * scale });
      c.getContext("2d").drawImage(img, 0, 0, c.width, c.height);
      resolve(c.toDataURL("image/jpeg", 0.82));
    };
    img.onerror = reject;
    img.src = URL.createObjectURL(file);
  });

const Text = ({ label, k, f, setF, hint, ...rest }) => (
  <label className="block text-xs font-mono uppercase tracking-widest text-slate-500">
    {label}
    <input value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} className={`${inputCls} normal-case tracking-normal font-sans`} data-testid={`product-form-${k}-input`} {...rest} />
    {hint && <span className="block mt-1 normal-case tracking-normal font-sans text-[11px] text-slate-400">{hint}</span>}
  </label>
);

const ImagePicker = ({ image, onPick }) => {
  const ref = useRef(null);
  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (file) onPick(await resizeImage(file));
    e.target.value = "";
  };
  return (
    <div className="flex items-center gap-4">
      <div className="w-20 h-20 rounded-xl bg-slate-100 overflow-hidden">{image && <img src={image} alt="" className="w-full h-full object-cover" />}</div>
      <input ref={ref} type="file" accept="image/*" className="hidden" onChange={onFile} data-testid="product-form-image-input" />
      <button type="button" onClick={() => ref.current.click()} className="h-10 px-4 rounded-full border border-slate-300 text-sm font-semibold flex items-center gap-2 hover:bg-slate-100" data-testid="product-form-image-btn">
        <Upload className="w-4 h-4" /> {image ? "Change photo" : "Upload photo"}
      </button>
    </div>
  );
};

export const ProductDialog = ({ open, product, onClose, onSaved }) => {
  const [f, setF] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) setF(toForm(product));
  }, [open, product]);

  const save = async (e) => {
    e.preventDefault();
    if (!f.image) return toast.error("Please upload a product photo");
    if (!f.name.trim()) return toast.error("Please enter a product name");
    if (f.points === "" || Number(f.points) < 0) return toast.error("Please enter points (0 or more)");
    setSaving(true);
    const body = { name: f.name, description: f.description, category: f.category, image: f.image, colors: split(f.colors), sizes: split(f.sizes), points: Number(f.points), active: f.active };
    try {
      if (product) await api.patch(`/admin/products/${product.id}`, body);
      else await api.post("/admin/products", body);
      toast.success(product ? "Product updated" : "Product added");
      onSaved();
      onClose();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="product-dialog">
        <DialogHeader><DialogTitle className="font-heading">{product ? "Edit product" : "Add product"}</DialogTitle></DialogHeader>
        <form onSubmit={save} className="space-y-4">
          <ImagePicker image={f.image} onPick={(image) => setF({ ...f, image })} />
          <Text label="Name" k="name" f={f} setF={setF} required />
          <label className="block text-xs font-mono uppercase tracking-widest text-slate-500">
            Description
            <textarea rows={3} value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} className={`${inputCls} h-auto py-2 normal-case tracking-normal font-sans`} data-testid="product-form-description-input" />
          </label>
          <div className="grid grid-cols-2 gap-4">
            <Text label="Category" k="category" f={f} setF={setF} />
            <Text label="Points" k="points" f={f} setF={setF} type="number" min="0" required />
          </div>
          <Text label="Colors" k="colors" f={f} setF={setF} hint="Comma separated, e.g. Black, White, Google Blue. Leave empty for none." />
          <Text label="Sizes" k="sizes" f={f} setF={setF} hint="Comma separated, e.g. S, M, L, XL. Leave empty for one-size items." />
          <label className="flex items-center gap-3 text-sm">
            <Switch checked={f.active} onCheckedChange={(v) => setF({ ...f, active: v })} data-testid="product-form-active-switch" /> Visible in store
          </label>
          <button type="submit" disabled={saving} className="w-full h-11 rounded-full bg-[#1A73E8] hover:bg-[#1557B0] text-white font-semibold flex items-center justify-center disabled:opacity-60" data-testid="product-form-save-btn">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : product ? "Save changes" : "Add product"}
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
};
